import { Box, Button, Divider, Grid, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { MemberUpdateActionEnum } from 'src/shared/models/enums/memberUpdateAction.enum';
import { createMemberUpdate, getMemberUpdateByMemberId } from 'src/shared/services/memberUpdateService';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import { MemberUpdateRequest } from '../models/memberUpdateRequest';
import { MemberUpdateRespons } from '../models/memberUpdateRespons';
import { MemberDetailsSummary } from './memberDetailsSummary';

type MemberUpdateModalProps = {
  modalOpen: boolean;
  memberUpdateId: number;
  onSave: () => void;
  onClose: () => void;
};

export const MemberUpdateModal = (props: MemberUpdateModalProps) => {
  const { modalOpen, memberUpdateId, onSave, onClose } = props;
  const { t } = useTranslation(['common', 'member']);
  const isMounted = useIsMounted();

  const [openAbortConfirmationDialog, setOpenAbortConfirmationDialog] = useState<boolean>(false);
  const [memberUpdate, setMemberUpdate] = useState<MemberUpdateRespons | undefined>();

  useEffect(() => {
    const fetchData = async () => {
      const result = await getMemberUpdateByMemberId(memberUpdateId);
      setMemberUpdate(result.data);
    };
    fetchData();
  }, [memberUpdateId, isMounted]);

  const onHandleSave = useCallback(() => {
    onSave();
  }, [onSave]);

  const onHandleClose = useCallback(() => {
    if (openAbortConfirmationDialog) {
      setOpenAbortConfirmationDialog(false);
    }
    onClose();
  }, [onClose, openAbortConfirmationDialog]);

  const handleMemberUpdate = async (isAborted: boolean) => {
    try {
      const result = await createMemberUpdate([
        {
          memberUpdate_id: memberUpdateId,
          action: isAborted ? MemberUpdateActionEnum.Reject : MemberUpdateActionEnum.Accept,
        } as MemberUpdateRequest,
      ]);
      if (isMounted()) {
        ConsoleHelper.log('result createMemberUpdate', result);
        toast.success(<>{isAborted ? t('member:memberUpdate.updateRejected') : t('member:memberUpdate.updateSuccess')}</>);
      }
    } catch (e) {
      if (isMounted()) ConsoleHelper.error(e);
    } finally {
      if (isMounted()) onHandleClose();
    }
  };

  return (
    <GenericDialog
      open={modalOpen}
      padding={0}
      dialogTitle={t('member:memberUpdate.reviewUpdatesHeader')}
      onClose={onHandleClose}
      onSave={onHandleSave}
      dialogContent={
        <Box sx={{ p: 3 }}>
          <GridWrapper>
            <Grid item xs>
              {memberUpdate && <MemberDetailsSummary member={memberUpdate.member} />}
            </Grid>
            <Divider flexItem orientation='vertical' sx={{ mb: -3 }} />
            <Grid item xs={8}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('member:memberUpdate.information')}</TableCell>
                    <TableCell>{t('member:memberUpdate.oldValue')}</TableCell>
                    <TableCell>{t('member:memberUpdate.newValue')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {memberUpdate &&
                    memberUpdate.changes.map((item) => (
                      <TableRow key={item.key}>
                        <TableCell>{item.text}</TableCell>
                        <TableCell sx={{ textDecoration: 'line-through' }}>{item.oldValue}</TableCell>
                        <TableCell sx={{ fontWeight: 600, color: (thm) => thm.palette.success.main }}>{item.newValue}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </Grid>
          </GridWrapper>
        </Box>
      }
      dialogActions={
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Button variant='outlined' disableElevation onClick={onHandleClose}>
            {t('common:button.cancel')}
          </Button>
          <Box>
            <Button
              variant='outlined'
              sx={{ mr: 1 }}
              disableElevation
              // endIcon={<DoNotDisturb fontSize='small' />}
              onClick={() => setOpenAbortConfirmationDialog(true)}
            >
              {t('common:button.abort')}
            </Button>
            <Button
              variant='contained'
              disableElevation
              //  endIcon={<Check fontSize='small' />}
              onClick={() => handleMemberUpdate(false)}
            >
              {t('common:button.accept')}
            </Button>
          </Box>
          <DeleteConfirmationDialog
            open={openAbortConfirmationDialog}
            title={t('member:memberUpdate.rejectConfirmTitle')}
            value=''
            modaltext={t('member:memberUpdate.rejectConfirmMessage')}
            onClose={(value) => {
              if (value) {
                handleMemberUpdate(true);
              } else {
                setOpenAbortConfirmationDialog(false);
              }
            }}
          />
        </Box>
      }
    />
  );
};
